import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShoppingCart, Star, Award, CheckCircle, TrendingUp } from 'lucide-react';
// CART CONTEXT IMPORT
import { useCart } from './CartContext'; 

// ----------------------
// Interfaces
// ----------------------
interface Product {
  id: number;
  sellerId: number;
  name: string;
  imageUrl: string;
  price: number;
  altText: string;
  rating: number; 
  reviewCount: number;
  discount: string; 
  companyName?: string;
}

// ----------------------
// ProductCard Component
// ----------------------
const ProductCard: React.FC<{ product: Product; index: number }> = ({ product, index }) => {
  
  // yaha 'as any' lagaya hai taaki context ki strictness ignore ho jaye
  const { addToCart } = useCart() as any; 

  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const discountedPrice = formatCurrency(product.price);
  const originalPrice = formatCurrency(product.price * 1.2); 

  // --- FIX: Updated handleAddToCart ---
  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // ERROR FIX: 'as any' added here
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.imageUrl,
      quantity: 1
    } as any); 
    
    // Optional: User ko batane ke liye
    // alert("Product added to cart!");
  };

  return (
    <article 
      className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-300 flex flex-col h-full hover:-translate-y-1"
      itemScope 
      itemType="https://schema.org/Product"
    >
      <div className="relative w-full aspect-[4/3] bg-gray-50 flex items-center justify-center overflow-hidden p-6">
        <div className="absolute top-0 left-0 bg-teal-600 text-white text-xs font-bold px-3 py-1.5 rounded-br-xl z-20 shadow-sm flex items-center gap-1">
           <Award className="w-3 h-3" />
           #{index + 1} Bestseller
        </div>

        <img
          src={product.imageUrl}
          alt={product.altText || product.name}
          className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110 mix-blend-multiply"
          loading="lazy"
          itemProp="image"
          onError={(e) => {e.currentTarget.src = 'https://via.placeholder.com/300?text=TradyFrog'}}
        />

        <div className="absolute inset-0 bg-teal-900/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

        <div className="absolute inset-x-0 bottom-4 px-4 flex items-center gap-3 
                        translate-y-12 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 
                        transition-all duration-300 ease-out z-20">
          <a
            href={`/store/${product.sellerId}`}
            className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold py-2.5 rounded-lg shadow-lg 
                       text-center transition-colors flex items-center justify-center gap-2"
          >
            Visit Store
          </a>
          
          <button
            onClick={handleAddToCart}
            className="bg-white hover:bg-teal-50 text-teal-700 hover:text-teal-900 p-2.5 rounded-lg shadow-lg border border-gray-100 transition-colors active:scale-95"
            title="Add to Cart"
          >
             <ShoppingCart className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="p-4 flex flex-col flex-grow border-t border-gray-50">
        <div className="flex justify-between items-start mb-2">
           <div className="flex flex-col">
              {product.companyName && (
                <div className="flex items-center gap-1 text-xs text-gray-500 font-medium uppercase tracking-wide">
                   {product.companyName}
                   <CheckCircle className="w-3 h-3 text-teal-500" aria-label="Verified Seller" />
                </div>
              )}
           </div>
           
           <div className="flex items-center gap-1 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded text-xs font-bold text-amber-700">
              {product.rating.toFixed(1)} <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
           </div>
        </div>

        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug mb-3 group-hover:text-teal-700 transition-colors" title={product.name}>
          <a href={`/store/${product.sellerId}`} itemProp="name">
            {product.name}
          </a>
        </h3>

        <div className="mt-auto pt-3 border-t border-dashed border-gray-200 flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-lg font-bold text-gray-900 leading-none">
              {discountedPrice}
            </span>
            <span className="text-xs text-gray-400 line-through mt-1">
              {originalPrice}
            </span>
          </div>
          <div className="bg-red-50 text-red-600 text-xs font-bold px-2 py-1 rounded-md border border-red-100">
            {product.discount} OFF
          </div>
        </div>
      </div>
    </article>
  );
};

// ----------------------
// Main Section
// ----------------------
const BestsellersSection: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBestsellers = async () => {
      try {
        const response = await axios.get('http://localhost:2026/api/bestsellers'); 
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching bestsellers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBestsellers();
  }, []);

  if (loading) return (
    <div className="w-full h-64 flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
    </div>
  );

  if (products.length === 0) return null; 

  return (
    <section className="my-16 max-w-[1400px] mx-auto px-4 md:px-6">
      <header className="flex flex-col sm:flex-row justify-between items-end mb-8 border-b border-gray-100 pb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
             <TrendingUp className="w-6 h-6 text-teal-600" />
             <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
               Bestselling <span className="text-teal-600">Products</span>
             </h2>
          </div>
          <p className="text-sm text-gray-500 max-w-xl">
             Explore the most popular products from our verified suppliers. Ranked by order volume and customer ratings.
          </p>
        </div>
        {/* <a href="/bestsellers" className="hidden sm:flex items-center gap-1 text-sm font-bold text-teal-600 hover:text-teal-800 hover:underline transition-all">
           View Leaderboard &rarr;
        </a> */}
      </header>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
        {products.map((product, index) => (
          <ProductCard key={product.id} product={product} index={index} />
        ))}
      </div>
    </section>
  );
};

export default BestsellersSection;